/**
 * Machine Simulator API Service
 * 
 * Centralized API calls for the CNC Machine Simulator.
 * This service can be configured to use either:
 * - Local development API (default)
 * - MachineMetrics platform API (when deployed)
 */

import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth headers (when using MM platform)
api.interceptors.request.use((config) => {
  // Add any auth headers here if needed
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ========== Machine Operations ==========

export const machineApi = {
  /**
   * Get all machines
   */
  getAllMachines: async () => {
    const response = await api.get('/machines');
    return response.data.machines;
  },

  /**
   * Get a single machine's status
   */
  getMachineStatus: async (machineName) => {
    const response = await api.get(`/${machineName}/status`);
    return response.data;
  },

  /**
   * Set machine execution state (ACTIVE/READY)
   */
  setExecution: async (machineName, state) => {
    const response = await api.post(`/${machineName}/set-execution`, { state });
    return response.data;
  },

  /**
   * Set feedrate override
   */
  setFeedrate: async (machineName, feedrate) => {
    const response = await api.post(`/${machineName}/set-feedrate`, { feedrate });
    return response.data;
  },

  /**
   * Set part count
   */
  setPartCount: async (machineName, partCount) => {
    const response = await api.post(`/${machineName}/part-count`, { partCount });
    return response.data;
  },

  /**
   * Set program
   */
  setProgram: async (machineName, program) => {
    const response = await api.post(`/${machineName}/set-program`, { program });
    return response.data;
  },

  /**
   * Set program header
   */
  setProgramHeader: async (machineName, header) => {
    const response = await api.post(`/${machineName}/set-program-header`, { header });
    return response.data;
  },

  /**
   * Set block delete mode
   */
  setBlockDelete: async (machineName, blockDelete) => {
    const response = await api.post(`/${machineName}/set-block-delete`, { blockDelete });
    return response.data;
  },

  /**
   * Set cycle mode (SINGLE/AUTO)
   */
  setCycleMode: async (machineName, cycleMode) => {
    const response = await api.post(`/${machineName}/set-cycle-mode`, { cycleMode });
    return response.data;
  },

  /**
   * Run a cycle
   */
  runCycle: async (machineName) => {
    const response = await api.post(`/${machineName}/run-cycle`);
    return response.data;
  },

  /**
   * Emergency stop
   */
  emergencyStop: async (machineName) => {
    const response = await api.post(`/${machineName}/emergency-stop`);
    return response.data;
  },

  /**
   * Get cycle progress
   */
  getCycleProgress: async (machineName) => {
    const response = await api.get(`/${machineName}/cycle-progress`);
    return response.data;
  },

  /**
   * Add a new machine
   */
  addMachine: async (name, state = {}) => {
    const response = await api.post('/add-machine', { name, state });
    return response.data;
  },

  /**
   * Delete a machine
   */
  deleteMachine: async (machineName) => {
    const response = await api.delete(`/${machineName}`);
    return response.data;
  },
};

// ========== Tool Operations ==========

export const toolApi = {
  /**
   * Get all tools for a machine
   */
  getAllTools: async (machineName) => {
    const response = await api.get(`/${machineName}/tools`);
    return response.data;
  },

  /**
   * Add or update a tool
   */
  updateTool: async (machineName, toolNumber, toolData) => {
    const response = await api.post(`/${machineName}/tools/${toolNumber}`, toolData);
    return response.data;
  },

  /**
   * Reset a tool to default
   */
  resetTool: async (machineName, toolNumber) => {
    const response = await api.delete(`/${machineName}/tools/${toolNumber}`);
    return response.data;
  },

  /**
   * Reset tool life counters
   */
  resetToolLife: async (machineName, toolsToReset = [1, 2, 3, 4]) => {
    const response = await api.post(`/${machineName}/reset-tool-life`, { toolsToReset });
    return response.data;
  },

  /**
   * Set tool life preset
   */
  setToolPreset: async (machineName, toolNumber, presetValue) => {
    const response = await api.post(`/${machineName}/set-tool-preset`, {
      toolNumber,
      presetValue,
    });
    return response.data;
  },
};

// ========== Program Operations ==========

export const programApi = {
  /**
   * Get all programs
   */
  getAllPrograms: async () => {
    const response = await api.get('/programs');
    return response.data;
  },

  /**
   * Add or update a program
   */
  updateProgram: async (programNumber, baseRunTime) => {
    const response = await api.post('/programs', { programNumber, baseRunTime });
    return response.data;
  },

  /**
   * Delete a program
   */
  deleteProgram: async (programNumber) => {
    const response = await api.delete(`/programs/${programNumber}`);
    return response.data;
  },
};

// ========== Company Operations ==========

export const companyApi = {
  /**
   * Get all companies for current user
   */
  getAll: async () => {
    const response = await api.get('/companies');
    return response.data.companies || response.data || [];
  },

  /**
   * Get a single company
   */
  getById: async (companyId) => {
    const response = await api.get(`/companies/${companyId}`);
    return response.data.company || response.data;
  },

  /**
   * Create a new company
   */
  create: async (name, mode = 'game') => {
    const response = await api.post('/companies', { name, mode });
    return response.data.company || response.data;
  },

  /**
   * Update company settings
   */
  update: async (companyId, updates) => {
    const response = await api.patch(`/companies/${companyId}`, updates);
    return response.data.company || response.data;
  },

  /**
   * Delete a company
   */
  delete: async (companyId) => {
    const response = await api.delete(`/companies/${companyId}`);
    return response.data;
  },

  /**
   * Get company machines
   */
  getMachines: async (companyId) => {
    const response = await api.get(`/companies/${companyId}/machines`);
    return response.data.machines || response.data || [];
  },

  /**
   * Get company operators
   */
  getOperators: async (companyId) => {
    const response = await api.get(`/companies/${companyId}/operators`);
    return response.data.operators || response.data || [];
  },
};

// ========== Metrics Operations ==========

export const metricsApi = {
  /**
   * Get KPI summary for a company
   */
  getKPIs: async (companyId) => {
    const response = await api.get(`/metrics/${companyId}/kpis`);
    return response.data;
  },

  /**
   * Get metrics history
   */
  getHistory: async (companyId, options = {}) => {
    const params = new URLSearchParams(options).toString();
    const response = await api.get(`/metrics/${companyId}${params ? `?${params}` : ''}`);
    return response.data.metrics || response.data || [];
  },

  /**
   * Record a metric
   */
  record: async (companyId, type, value, metadata = {}) => {
    const response = await api.post(`/metrics/${companyId}/record`, { type, value, metadata });
    return response.data;
  },
};

// ========== Events Operations ==========

export const eventsApi = {
  /**
   * Get event templates
   */
  getTemplates: async () => {
    const response = await api.get('/events/templates');
    return response.data.templates || response.data || [];
  },

  /**
   * Get active events for a company
   */
  getActive: async (companyId) => {
    const response = await api.get(`/events/${companyId}/active`);
    return response.data.events || response.data || [];
  },

  /**
   * Trigger an event
   */
  trigger: async (companyId, templateCode, customData = {}) => {
    const response = await api.post(`/events/${companyId}/trigger`, { templateCode, customData });
    return response.data;
  },

  /**
   * Resolve an event
   */
  resolve: async (companyId, eventId, notes = '') => {
    const response = await api.post(`/events/${companyId}/${eventId}/resolve`, { resolutionNotes: notes });
    return response.data;
  },
};

// ========== Production Operations ==========

export const productionApi = {
  /**
   * Get production config
   */
  getConfig: async () => {
    const response = await api.get('/production/config');
    return response.data;
  },

  /**
   * Calculate production summary
   */
  getSummary: async (params) => {
    const response = await api.post('/production/summary', params);
    return response.data;
  },

  /**
   * Simulate production
   */
  simulate: async (params) => {
    const response = await api.post('/production/simulate', params);
    return response.data;
  },
};

// Export default instance for direct axios access if needed
export default api;
